import { useMemo } from "react";

import AgChartsOptionsChartCard from "./AgChartsOptionsChartCard";
import AnalyticsQueryChartCard from "./AnalyticsQueryChartCard";
import { buildAgChartsOptionsChart } from "./chartOptions/agChartsOptionsChart";
import { ANALYTICS_GRAPH_GRID_CONTAINER_MIN_WIDTH } from "./constants";
import { sanitizeChartDownloadFileName } from "./utils/sanitizeChartDownloadFileName";

import type {
  AgChartsGraphArtifact,
  AnalyticsGraphToolChartData,
  DashboardChartSpecRequest,
} from "@/queries/analytics-custom-charts-types";
import {
  DashboardChartMode,
  getAgChartsGraphArtifactFilterGroup,
  splitGraphChartsByMode,
} from "@/queries/analytics-custom-charts-types";
import { cn } from "@/lib/utils";

interface AgChartsGraphGridProps {
  artifact: AgChartsGraphArtifact;
  compact?: boolean;
  className?: string;
  onChartDataChange?: (chartData: AnalyticsGraphToolChartData) => void;
}

type ChartEntry =
  | {
      kind: DashboardChartMode.AnalyticsQuery;
      chart: DashboardChartSpecRequest;
    }
  | {
      kind: DashboardChartMode.AgChartsOptions;
      chart: DashboardChartSpecRequest;
    };

const GRID_TWO_COLUMN_CLASS = `[@container(min-width:${ANALYTICS_GRAPH_GRID_CONTAINER_MIN_WIDTH})]:grid-cols-2`;
const GRID_TWO_COLUMN_SPAN_CLASS = `[@container(min-width:${ANALYTICS_GRAPH_GRID_CONTAINER_MIN_WIDTH})]:col-span-2`;

const AgChartsGraphGrid = ({
  artifact,
  compact = false,
  className,
  onChartDataChange,
}: AgChartsGraphGridProps) => {
  const { analyticsQueryCharts, agChartsOptionsCharts } = useMemo(
    () => splitGraphChartsByMode(artifact.charts),
    [artifact.charts],
  );

  const chartEntries = useMemo<ChartEntry[]>(
    () => [
      ...analyticsQueryCharts.map(chart => ({
        kind: DashboardChartMode.AnalyticsQuery,
        chart,
      })),
      ...agChartsOptionsCharts
        .filter(chart => chart.agChartsOptions)
        .map(chart => ({
          kind: DashboardChartMode.AgChartsOptions,
          chart,
        })),
    ],
    [agChartsOptionsCharts, analyticsQueryCharts],
  );

  if (chartEntries.length === 0) {
    return null;
  }

  const totalChartCount = chartEntries.length;
  const isSingleChart = totalChartCount === 1;
  const shouldSpanLastChartTwoColumns =
    !compact && totalChartCount > 1 && totalChartCount % 2 === 1;

  const getChartCardClassName = (index: number) =>
    cn(
      isSingleChart && "w-full",
      shouldSpanLastChartTwoColumns && index === totalChartCount - 1 && GRID_TWO_COLUMN_SPAN_CLASS,
    );

  return (
    <div className={cn("@container w-full", className)}>
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          !compact && !isSingleChart && GRID_TWO_COLUMN_CLASS,
        )}
      >
        {chartEntries.map((entry, index) => {
          switch (entry.kind) {
            case DashboardChartMode.AnalyticsQuery:
              return (
                <AnalyticsQueryChartCard
                  key={entry.chart.id}
                  chart={entry.chart}
                  dataSource={artifact.dataSource ?? artifact.data_source}
                  artifactTitle={artifact.title}
                  artifactFilterGroup={getAgChartsGraphArtifactFilterGroup(artifact)}
                  className={getChartCardClassName(index)}
                  onChartDataChange={onChartDataChange}
                />
              );
            case DashboardChartMode.AgChartsOptions: {
              const options = entry.chart.agChartsOptions;
              if (!options) {
                return null;
              }

              const dataLength = Array.isArray(options.data) ? options.data.length : 0;
              const downloadFileName = sanitizeChartDownloadFileName(
                artifact.title,
                entry.chart.title,
              );

              return (
                <AgChartsOptionsChartCard
                  key={entry.chart.id}
                  title={entry.chart.title}
                  downloadFileName={downloadFileName}
                  chartOptions={buildAgChartsOptionsChart(options)}
                  resetKey={`${entry.chart.id}:${dataLength}`}
                  isEmpty={dataLength === 0}
                  className={getChartCardClassName(index)}
                />
              );
            }
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
};

export default AgChartsGraphGrid;
